/**
 * Centralized Error Handling System for LifeFinder App
 * Provides consistent error management across the entire application
 */

import { ErrorInfo as ReactErrorInfo } from 'react';

// Error Types
export enum ErrorType {
  NETWORK = 'NETWORK',
  AUTHENTICATION = 'AUTHENTICATION',
  VALIDATION = 'VALIDATION',
  API = 'API',
  WEBSOCKET = 'WEBSOCKET',
  UNKNOWN = 'UNKNOWN',
}

// Severity Levels
export enum ErrorSeverity {
  LOW = 'LOW', // User can continue working
  MEDIUM = 'MEDIUM', // Feature is impaired but app functional
  HIGH = 'HIGH', // Critical feature broken
  CRITICAL = 'CRITICAL', // App unusable
}

/**
 * Get default user message based on error type
 * Extracted as standalone function to avoid ts-jest/isolatedModules issues
 */
const getDefaultUserMessage = (type: ErrorType): string => {
  switch (type) {
    case ErrorType.NETWORK:
      return 'Connection problem. Please check your internet connection.';
    case ErrorType.AUTHENTICATION:
      return 'You need to log in again.';
    case ErrorType.VALIDATION:
      return 'Please check that all information is filled in correctly.';
    case ErrorType.API:
      return 'A technical error occurred. Please try again later.';
    case ErrorType.WEBSOCKET:
      return 'Real-time updates are temporarily unavailable.';
    default:
      return 'An unexpected error occurred. Please try again or contact support.';
  }
};

// Custom Error Classes
export class LifeFinderError extends Error {
  public readonly type: ErrorType;
  public readonly severity: ErrorSeverity;
  public readonly timestamp: Date;
  public readonly userMessage: string;
  public readonly technicalDetails?: any;

  constructor(
    message: string,
    type: ErrorType = ErrorType.UNKNOWN,
    severity: ErrorSeverity = ErrorSeverity.MEDIUM,
    userMessage?: string,
    technicalDetails?: any
  ) {
    super(message);
    this.name = 'LifeFinderError';
    this.type = type;
    this.severity = severity;
    this.timestamp = new Date();
    this.userMessage = userMessage || getDefaultUserMessage(type);
    this.technicalDetails = technicalDetails;
  }
}

// API Error with HTTP Status
export class APIError extends LifeFinderError {
  public readonly statusCode: number;
  public readonly endpoint: string;

  constructor(
    message: string,
    statusCode: number,
    endpoint: string,
    userMessage?: string,
    technicalDetails?: any
  ) {
    const severity = APIError.getSeverityFromStatus(statusCode);
    super(message, ErrorType.API, severity, userMessage, technicalDetails);

    this.statusCode = statusCode;
    this.endpoint = endpoint;
  }

  private static getSeverityFromStatus(statusCode: number): ErrorSeverity {
    if (statusCode >= 500) return ErrorSeverity.HIGH;
    if (statusCode === 401 || statusCode === 403) return ErrorSeverity.CRITICAL;
    if (statusCode >= 400) return ErrorSeverity.MEDIUM;
    return ErrorSeverity.LOW;
  }
}

// Error Reporter Interface
interface ErrorReporter {
  report(error: LifeFinderError): void;
}

// Console Reporter (Development)
class ConsoleErrorReporter implements ErrorReporter {
  report(error: LifeFinderError): void {
    const prefix = `[${error.severity}] ${error.type}`;

    if (error.severity === ErrorSeverity.CRITICAL) {
      console.error(`${prefix}:`, error.message, error.technicalDetails);
    } else if (error.severity === ErrorSeverity.HIGH) {
      console.warn(`${prefix}:`, error.message);
    } else {
      console.log(`${prefix}:`, error.message);
    }
  }
}

// User Notification Reporter
class NotificationErrorReporter implements ErrorReporter {
  report(error: LifeFinderError): void {
    // Integrate with your notification system
    // For now, we'll use a simple approach
    if (error.severity === ErrorSeverity.CRITICAL || error.severity === ErrorSeverity.HIGH) {
      // Show user-visible error message
      this.showUserNotification(error);
    }
  }

  private showUserNotification(error: LifeFinderError): void {
    // Integration point for UI notifications
    // Could trigger toast notifications, modal dialogs, etc.
    console.log(`USER NOTIFICATION: ${error.userMessage}`);
  }
}

// Centralized Error Manager
class ErrorManager {
  private static instance: ErrorManager;
  private reporters: ErrorReporter[] = [];

  private constructor() {
    // Default reporters
    this.reporters.push(new ConsoleErrorReporter());
    this.reporters.push(new NotificationErrorReporter());
  }

  static getInstance(): ErrorManager {
    if (!ErrorManager.instance) {
      ErrorManager.instance = new ErrorManager();
    }
    return ErrorManager.instance;
  }

  addReporter(reporter: ErrorReporter): void {
    this.reporters.push(reporter);
  }

  handle(error: Error | LifeFinderError | unknown): LifeFinderError {
    let processedError: LifeFinderError;

    if (error instanceof LifeFinderError) {
      processedError = error;
    } else if (error instanceof Error) {
      processedError = this.convertToLifeFinderError(error);
    } else {
      processedError = new LifeFinderError(
        'Unknown error occurred',
        ErrorType.UNKNOWN,
        ErrorSeverity.MEDIUM,
        undefined,
        error
      );
    }

    // Report to all configured reporters
    this.reporters.forEach((reporter) => {
      try {
        reporter.report(processedError);
      } catch (reporterError) {
        console.error('Error in error reporter:', reporterError);
      }
    });

    return processedError;
  }

  private convertToLifeFinderError(error: Error): LifeFinderError {
    // Convert common error types
    if (error.message.includes('fetch') || error.message.includes('network')) {
      return new LifeFinderError(
        error.message,
        ErrorType.NETWORK,
        ErrorSeverity.HIGH,
        undefined,
        error
      );
    }

    if (error.message.includes('401') || error.message.includes('unauthorized')) {
      return new LifeFinderError(
        error.message,
        ErrorType.AUTHENTICATION,
        ErrorSeverity.CRITICAL,
        undefined,
        error
      );
    }

    return new LifeFinderError(
      error.message,
      ErrorType.UNKNOWN,
      ErrorSeverity.MEDIUM,
      undefined,
      error
    );
  }
}

// Singleton instance
export const errorManager = ErrorManager.getInstance();

// Convenient functions for common use cases
export const handleError = (error: unknown): LifeFinderError => {
  return errorManager.handle(error);
};

export const createAPIError = (
  message: string,
  statusCode: number,
  endpoint: string,
  technicalDetails?: any
): APIError => {
  return new APIError(message, statusCode, endpoint, undefined, technicalDetails);
};

export const createNetworkError = (
  message: string = 'Network request failed',
  technicalDetails?: any
): LifeFinderError => {
  return new LifeFinderError(
    message,
    ErrorType.NETWORK,
    ErrorSeverity.HIGH,
    'Connection problem. Please check your internet connection and try again.',
    technicalDetails
  );
};

export const createAuthError = (
  message: string = 'Authentication failed',
  technicalDetails?: any
): LifeFinderError => {
  return new LifeFinderError(
    message,
    ErrorType.AUTHENTICATION,
    ErrorSeverity.CRITICAL,
    'You need to log in again to continue.',
    technicalDetails
  );
};

export const handleReactError = (error: Error, errorInfo: ReactErrorInfo): LifeFinderError => {
  return errorManager.handle(
    new LifeFinderError(
      `React Error: ${error.message}`,
      ErrorType.UNKNOWN,
      ErrorSeverity.HIGH,
      'An unexpected error occurred in the application. The page will be reloaded.',
      { error, errorInfo }
    )
  );
};
